import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Modal, 
  Form, 
  Select, 
  Tag, 
  Typography, 
  message, 
  DatePicker, 
  InputNumber,
  Switch,
  Tooltip,
  Row,
  Col,
  Card,
  Divider
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  GiftOutlined,
  DollarOutlined,
  PercentageOutlined,
  SettingOutlined,
  CalendarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { billingService } from '../../services/billingService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PromoCodeManagement = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState(null);
  const [form] = Form.useForm();

  // 获取促销码列表
  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const response = await billingService.getAllPromoCodes();
      // 转换数据格式以适配表格显示
      const formattedPromoCodes = response.map(promo => ({
        id: promo.id,
        code: promo.code,
        description: promo.description,
        type: promo.type,
        value: promo.value,
        minAmount: promo.minAmount,
        maxDiscount: promo.maxDiscount,
        usageLimit: promo.usageLimit,
        usedCount: promo.usedCount,
        status: promo.status,
        validFrom: promo.validFrom,
        validUntil: promo.validUntil,
        isGlobal: promo.isGlobal,
        createdAt: new Date(promo.createdAt).toLocaleString('en-US'),
        updatedAt: new Date(promo.updatedAt).toLocaleString('en-US')
      }));
      setPromoCodes(formattedPromoCodes);
    } catch (error) {
      console.error('获取促销码列表失败:', error);
      message.error('Failed to fetch promo codes');
    } finally {
      setLoading(false);
    }
  };

  // 组件加载时获取促销码列表
  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const columns = [
    {
      title: 'Promo Code',
      dataIndex: 'code',
      key: 'code',
      render: (text, record) => (
        <Space>
          <GiftOutlined style={{ color: '#1890ff' }} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.description}
            </Text>
          </div>
        </Space>
      ),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) =>
        record.code.toLowerCase().includes(value.toLowerCase()) ||
        record.description.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type, record) => (
        <Space>
          {type === 'percentage' ? (
            <PercentageOutlined style={{ color: '#52c41a' }} />
          ) : (
            <DollarOutlined style={{ color: '#1890ff' }} />
          )}
          <Tag color={type === 'percentage' ? 'green' : 'blue'}>
            {type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
          </Tag>
        </Space>
      ),
      filters: [
        { text: 'Percentage', value: 'percentage' },
        { text: 'Fixed Amount', value: 'fixed' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Discount Value',
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => (
        <Text strong>
          {record.type === 'percentage' ? `${value}%` : `$${value}`}
        </Text>
      ),
      sorter: (a, b) => a.value - b.value,
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => (
        <div>
          <div>
            <Text type="secondary">Used: {record.usedCount}</Text>
          </div>
          <div>
            <Text type="secondary">
              Limit: {record.usageLimit === -1 ? 'Unlimited' : record.usageLimit}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          active: { color: 'green', text: 'Active' },
          inactive: { color: 'default', text: 'Inactive' },
          expired: { color: 'red', text: 'Expired' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Expired', value: 'expired' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Global',
      dataIndex: 'isGlobal',
      key: 'isGlobal',
      render: (isGlobal) => (
        <Tag color={isGlobal ? 'blue' : 'default'}>
          {isGlobal ? 'Yes' : 'No'}
        </Tag>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.isGlobal === value,
    },
    {
      title: 'Validity',
      key: 'validity',
      render: (_, record) => (
        <div>
          {record.validFrom && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Start: {new Date(record.validFrom).toLocaleDateString('en-US')}
              </Text>
            </div>
          )}
          {record.validUntil && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                End: {new Date(record.validUntil).toLocaleDateString('en-US')}
              </Text>
            </div>
          )}
          {!record.validFrom && !record.validUntil && (
            <Text type="secondary">Always Valid</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleAdd = () => {
    setEditingPromoCode(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (promoCode) => {
    setEditingPromoCode(promoCode);
    form.setFieldsValue({
      code: promoCode.code,
      description: promoCode.description,
      type: promoCode.type,
      value: promoCode.value,
      minAmount: promoCode.minAmount,
      maxDiscount: promoCode.maxDiscount,
      usageLimit: promoCode.usageLimit === -1 ? null : promoCode.usageLimit,
      status: promoCode.status,
      validFrom: promoCode.validFrom ? dayjs(promoCode.validFrom) : null,
      validUntil: promoCode.validUntil ? dayjs(promoCode.validUntil) : null,
      isGlobal: promoCode.isGlobal
    });
    setIsModalVisible(true);
  };

  const handleDelete = (promoCode) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete promo code "${promoCode.code}"? This action cannot be undone.`,
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await billingService.deletePromoCode(promoCode.id);
          message.success('Promo code deleted successfully');
          fetchPromoCodes(); // 重新获取促销码列表
        } catch (error) {
          console.error('删除促销码失败:', error);
          message.error('Failed to delete promo code');
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // 处理日期格式
      const promoData = {
        ...values,
        usageLimit: values.usageLimit || -1,
        validFrom: values.validFrom ? values.validFrom.toISOString() : null,
        validUntil: values.validUntil ? values.validUntil.toISOString() : null,
      };
      
      if (editingPromoCode) {
        // 编辑促销码
        await billingService.updatePromoCode(editingPromoCode.id, promoData);
        message.success('Promo code updated successfully');
      } else {
        // 添加促销码
        await billingService.createPromoCode(promoData);
        message.success('Promo code added successfully');
      }
      
      setIsModalVisible(false);
      fetchPromoCodes(); // 重新获取促销码列表
    } catch (error) {
      console.error('保存促销码失败:', error);
      message.error('Failed to save promo code');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  // 渲染左右布局的表单
  const renderFormContent = () => (
    <Row gutter={24}>
      {/* 左侧：基础信息 */}
      <Col span={12}>
        <Card 
          title={
            <Space>
              <GiftOutlined />
              Basic Info
            </Space>
          }
          size="small"
          style={{ height: '100%' }}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              type: 'percentage',
              status: 'active',
              isGlobal: false,
            }}
          >
            <Form.Item
              name="code"
              label="Promo Code"
              rules={[{ required: true, message: 'Please enter promo code!' }]}
            >
              <Input placeholder="Enter promo code (auto-converted to uppercase)" />
            </Form.Item>
            
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter description!' }]}
            >
              <Input placeholder="Enter promo code description" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="type"
                  label="Discount Type"
                  rules={[{ required: true, message: 'Please select discount type!' }]}
                >
                  <Select placeholder="Select discount type">
                    <Option value="percentage">Percentage Discount</Option>
                    <Option value="fixed">Fixed Amount Discount</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="value"
                  label="Discount Value"
                  rules={[{ required: true, message: 'Please enter discount value!' }]}
                >
                  <InputNumber
                    placeholder="Enter discount value"
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status!' }]}
            >
              <Select placeholder="Select status">
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
                <Option value="expired">Expired</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="isGlobal"
              label="Global Promo Code"
              valuePropName="checked"
              extra="When enabled, all users can use this promo code"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Card>
      </Col>

      {/* 右侧：限制条件和有效期 */}
      <Col span={12}>
        <Card 
          title={
            <Space>
              <SettingOutlined />
              Restrictions
            </Space>
          }
          size="small"
        >
          <Form layout="vertical">
            <Form.Item
              name="minAmount"
              label="Minimum Order Amount"
              extra="Set the minimum order amount required to use this promo code"
            >
              <InputNumber
                placeholder="Minimum order amount (optional)"
                style={{ width: '100%' }}
                min={0}
                precision={2}
              />
            </Form.Item>

            <Form.Item
              name="maxDiscount"
              label="Maximum Discount Amount"
              extra="Limit the maximum discount amount per use"
            >
              <InputNumber
                placeholder="Maximum discount amount (optional)"
                style={{ width: '100%' }}
                min={0}
                precision={2}
              />
            </Form.Item>

            <Form.Item
              name="usageLimit"
              label="Usage Limit"
              extra="Set the total usage count for the promo code, leave empty for unlimited"
            >
              <InputNumber
                placeholder="Usage limit (leave empty for unlimited)"
                style={{ width: '100%' }}
                min={-1}
              />
            </Form.Item>
          </Form>
        </Card>

        <Card 
          title={
            <Space>
              <CalendarOutlined />
              Validity Period
            </Space>
          }
          size="small"
          style={{ marginTop: 16, marginBottom: 0 }}
        >
          <Form layout="vertical">
            <Form.Item
              name="validFrom"
              label="Valid From"
              extra="Leave empty to take effect immediately"
            >
              <DatePicker
                showTime={{
                  format: 'HH:mm:ss',
                  defaultValue: dayjs('00:00:00', 'HH:mm:ss'),
                }}
                placeholder="Select start time (optional)"
                style={{ width: '100%' }}
                format="YYYY-MM-DD HH:mm:ss"
                suffixIcon={<CalendarOutlined style={{ color: '#1890ff' }} />}
                allowClear
                showToday={false}
              />
            </Form.Item>

            <Form.Item
              name="validUntil"
              label="Valid Until"
              extra="Leave empty for permanent validity"
            >
              <DatePicker
                showTime={{
                  format: 'HH:mm:ss',
                  defaultValue: dayjs('23:59:59', 'HH:mm:ss'),
                }}
                placeholder="Select end time (optional)"
                style={{ width: '100%' }}
                format="YYYY-MM-DD HH:mm:ss"
                suffixIcon={<CalendarOutlined style={{ color: '#1890ff' }} />}
                allowClear
                showToday={false}
              />
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );

  return (
    <div className="admin-module">
      <div className="admin-module-header">
        <Title level={3} className="admin-module-title">Promo Code Management</Title>
        <Text className="admin-module-description">
          Manage system promo codes, including adding, editing, and deleting promo code information
        </Text>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input.Search
            placeholder="Search promo code or description"
            allowClear
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Promo Code
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={promoCodes}
        loading={loading}
        rowKey="id"
        pagination={{
          total: promoCodes.length,
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} records`,
        }}
      />

      <Modal
        title={
          <Space>
            <GiftOutlined />
            {editingPromoCode ? 'Edit Promo Code' : 'Add Promo Code'}
          </Space>
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Confirm"
        cancelText="Cancel"
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        destroyOnClose
      >
        {renderFormContent()}
      </Modal>
    </div>
  );
};

export default PromoCodeManagement;